import { Bot, User, MapPin } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { IMessage } from '../types/types';
import { formatMessageTime } from '../utils/helpers';
import WorkflowMessage from './WorkflowMessage';

interface ChatMessageProps {
  message: IMessage;
  botLogo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  textColor?: string;
  isWorkflowActive: boolean;
  isLoading: boolean;
  isLastMessage?: boolean;
  onWorkflowButtonClick?: (buttonId: string, buttonLabel?: string, contextMessageId?: string) => void;
  onWorkflowListSelect?: (itemId: string, itemTitle?: string, contextMessageId?: string) => void;
  onWorkflowAddressSubmit?: (address: string) => void;
  onWorkflowLocationCapture?: (latitude: number, longitude: number) => void;
  onWorkflowMediaUpload?: (file: File) => Promise<void>;
  fallbackImage?: string;
  containedModal?: boolean; // New prop to control modal positioning
}

export default function ChatMessage({
  message,
  botLogo,
  primaryColor,
  secondaryColor,
  textColor,
  // isWorkflowActive,
  isLoading,
  // isLastMessage = false,
  onWorkflowButtonClick,
  onWorkflowListSelect,
  onWorkflowAddressSubmit,
  onWorkflowLocationCapture,
  onWorkflowMediaUpload,
  fallbackImage,
  containedModal = false
}: ChatMessageProps) {
  // Render workflow messages
  if (message.type === 'workflow' && message.workflowData) {
    return (
      <div className="flex gap-3 items-start">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: primaryColor }}
        >
          {botLogo ? (
            <img
              src={botLogo}
              onError={(e) => (e.currentTarget.src = fallbackImage || '')}
              alt="Bot"
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <Bot size={16} className="text-white" />
          )}
        </div>
        <div className="flex-1 flex flex-col items-start min-w-0">
          <div className="w-fit max-w-[80%] bg-white dark:bg-gray-700 rounded-sm rounded-tl-none shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden break-all">
            <WorkflowMessage
              message={{...message.workflowData, contextMessageId: message._id }}
              onButtonClick={onWorkflowButtonClick}
              onListItemSelect={onWorkflowListSelect}
              onAddressSubmit={onWorkflowAddressSubmit}
              onLocationCapture={onWorkflowLocationCapture}
              onMediaUpload={onWorkflowMediaUpload}
              // disabled={!isLastMessage || !isWorkflowActive || isLoading}
              disabled={isLoading}
              primaryColor={primaryColor}
              containedModal={containedModal}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-1">
            {formatMessageTime(message.createdAt)}
          </p>
        </div>
      </div>
    );
  }

  // Render regular messages
  return (
    <div className={`flex gap-3 items-start ${message.isFromUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${message.isFromUser ? '' : 'bg-gray-200'
          }`}
        style={!message.isFromUser ? {} : { backgroundColor: secondaryColor }}
      >
        {message.isFromUser ? (
          <User size={16} className="text-white" />
        ) : botLogo ? (
          <img
            src={botLogo}
            onError={(e) => (e.currentTarget.src = fallbackImage || '')}
            alt="Bot"
            className="w-full h-full object-cover rounded-full"
          />
        ) : (
          <Bot size={16} className="text-white" />
        )}
      </div>
      <div className={`flex-1 flex flex-col min-w-0 ${message.isFromUser ? 'items-end' : 'items-start'}`}>
        <div
          className={`w-fit max-w-[75%] p-3 shadow-sm break-all ${message.isFromUser
            ? 'bg-gray-100 rounded-sm rounded-tr-none'
            : 'bg-white dark:bg-gray-700/60 border border-gray-100 dark:border-gray-700 rounded-sm rounded-tl-none'
            }`}
          style={{ backgroundColor: message.isFromUser ? secondaryColor : '' }}
        >
          {/* Media Messages */}
          {message.type === 'image' && message.mediaUrl && (
            <div className="mb-2">
              <div className="relative w-full h-48 overflow-hidden rounded-lg">
                <img
                  src={message.mediaUrl}
                  alt={message.content}
                  className="w-full h-full object-cover"
                />
              </div>
              {message.content && message.content !== message.mediaUrl && (
                <p className="mt-2 text-sm tex-gray-900 dark:text-gray-100" style={{ color: message.isFromUser ? textColor : undefined }}>
                  {message.content}
                </p>
              )}
            </div>
          )}

          {message.type === 'video' && message.mediaUrl && (
            <div className="mb-2">
              <div className="relative w-full h-48 overflow-hidden rounded-lg">
                <video
                  src={message.mediaUrl}
                  controls
                  className="w-full h-full object-cover"
                />
              </div>
              {message.content && message.content !== message.mediaUrl && (
                <p className="mt-2 text-sm tex-gray-900 dark:text-gray-100" style={{ color: message.isFromUser ? textColor : undefined }}>
                  {message.content}
                </p>
              )}
            </div>
          )}

          {message.type === 'audio' && message.mediaUrl && (
            <div className="mb-2">
              <audio src={message.mediaUrl} controls className="max-w-full" />
              {message.content && message.content !== message.mediaUrl && (
                <p className="mt-2 text-sm tex-gray-900 dark:text-gray-100" style={{ color: message.isFromUser ? textColor : undefined }}>
                  {message.content}
                </p>
              )}
            </div>
          )}

          {message.type === 'document' && message.mediaUrl && (
            <div className="mb-2">
              <a
                href={message.mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm hover:underline"
                style={{ color: message.isFromUser ? textColor : '#2563eb' }}
              >
                📎 {message.content}
              </a>
            </div>
          )}

          {message.type === 'location' && message.location && (
            <div className="w-full max-w-[250px] overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="relative h-32 w-full overflow-hidden bg-gray-100 dark:bg-gray-700">
                <iframe
                  title="Location Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://maps.google.com/maps?q=${message.location.latitude},${message.location.longitude}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                ></iframe>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${message.location.latitude},${message.location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 z-10"
                ></a>
              </div>
              <div className="p-2 flex items-center gap-2">
                <div className="bg-emerald-100 dark:bg-emerald-900/30 p-1.5 rounded-full shrink-0">
                  <MapPin size={16} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {message.location.address || 'Shared Location'}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-300 truncate">
                    {message.location.latitude.toFixed(4)}, {message.location.longitude.toFixed(4)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Text Messages */}
          {(!message.type || message.type === 'text') && message.isFromUser && (
            <p
              className="text-sm text-gray-800 dark:text-white"
              style={{ color: message.isFromUser ? textColor : 'gray' }}
            >
              {message.content}
            </p>
          )}

          {(!message.type || message.type === 'text') && !message.isFromUser && (
            <div className="text-sm text-gray-800 dark:text-white prose prose-sm max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                  code: ({ children }) => (
                    <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-xs">
                      {children}
                    </code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-gray-100 dark:bg-gray-700 p-2 rounded my-2 overflow-x-auto">
                      {children}
                    </pre>
                  ),
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 underline hover:opacity-80 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1 ml-1">{formatMessageTime(message.createdAt)}</p>
      </div>
    </div>
  );
}
